/**
 * Modal d'édition d'un bloc pub déjà placé en grille.
 *
 * Charge le bloc via `GET /ad-blocks/items/:itemId`, permet de :
 *   - retirer un spot
 *   - réordonner les spots (flèches ▲/▼)
 *   - ajouter un nouveau spot depuis la bibliothèque (campagnes ACTIVE)
 *
 * À la sauvegarde, envoie la nouvelle liste ordonnée à
 * `PATCH /ad-blocks/items/:itemId/spots` qui recalcule la durée du bloc et
 * remplace les placements de manière transactionnelle.
 *
 * Le backend refuse l'édition si l'item est ON_AIR / LOADING / DONE — la modal
 * affiche alors un message d'erreur clair.
 */

import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useStore } from '../../store/index.js'
import { adBlocksApi, spotsApi, type AdBlockDetail, type AdBlockRealignSide } from '../../lib/adsApi.js'
import { adBlockBlackDurationSec, adBlockTotalDurationSec } from '../../lib/adBlockTiming.js'
import { ModalShell } from '../common/ModalShell.js'
import { MissingFileBadge } from '../common/MissingFileBadge.js'
import type { AssetPresenceEntry } from '../../types/index.js'
import { Spinner } from '../common/Spinner.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'

// ─── Types locaux ────────────────────────────────────────────────────────────

interface SpotEntry {
  spotId:         string
  title:          string
  durationSec:    number
  campaignName:   string | null
  advertiserName: string | null
  /** Identifiant interne stable pour le drag/drop (un même spotId peut être présent N fois). */
  rowKey:         string
}

interface CatalogSpot {
  id:             string
  title:          string
  durationSec:    number
  campaign?:      { id: string; name: string; startDate?: string; endDate?: string } | null
  advertiser?:    { id: string; name: string } | null
  /** Présence du fichier sur disque. `null` = non suivi (chemin vide ou flux). */
  presence?:      AssetPresenceEntry | null
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface AdBlockEditModalProps {
  itemId:    string
  channelId: string
  onClose:   () => void
  onSaved:   () => void
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtSec(sec: number): string {
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return s ? `${m}m ${s}s` : `${m}m`
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

let rowKeyCounter = 0
const newRowKey = () => `row-${++rowKeyCounter}-${Date.now()}`

// ─── Composant principal ─────────────────────────────────────────────────────

export const AdBlockEditModal = memo(function AdBlockEditModal({ itemId, channelId, onClose, onSaved }: AdBlockEditModalProps) {
  const pushToast              = useStore(s => s.pushToast)
  const incrementGridUpdateSeq = useStore(s => s.incrementGridUpdateSeq)

  const [detail,        setDetail]        = useState<AdBlockDetail | null>(null)
  const [entries,       setEntries]       = useState<SpotEntry[]>([])
  const [catalog,       setCatalog]       = useState<CatalogSpot[]>([])
  const [loading,       setLoading]       = useState(true)
  const [saving,        setSaving]        = useState(false)
  const [showAdd,       setShowAdd]       = useState(false)
  const [search,        setSearch]        = useState('')
  const [error,         setError]         = useState<string | null>(null)
  const [askSide,       setAskSide]       = useState(false)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  // Charge le bloc + le catalogue de spots disponibles pour l'ajout
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      adBlocksApi.getItem(itemId),
      spotsApi.list({ channelId }),
    ])
      .then(([d, c]) => {
        if (cancelled) return
        setDetail(d)
        setEntries(d.spots.map(s => ({
          spotId:         s.spotId,
          title:          s.title,
          durationSec:    s.durationSec,
          campaignName:   s.campaignName,
          advertiserName: s.advertiserName,
          rowKey:         newRowKey(),
        })))
        // Le catalogue ne contient que les spots actifs **rattachés à une campagne**.
        // Les spots standalone orphelins (héritage de l'ancienne UI supprimée)
        // ne sont plus exposés.
        setCatalog(Array.isArray(c) ? c.filter((s: any) => s.active && s.campaign) : [])
      })
      .catch(err => {
        if (!cancelled) setError(err?.message ?? 'Impossible de charger le bloc')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [itemId, channelId])

  // ── Calculs dérivés ────────────────────────────────────────────────────────

  const totalDuration = useMemo(() => adBlockTotalDurationSec(entries.map(e => e.durationSec)), [entries])
  const blackDuration = useMemo(() => adBlockBlackDurationSec(entries.length), [entries.length])
  const newEndTime    = useMemo(() => {
    if (!detail) return null
    return new Date(new Date(detail.startTime).getTime() + totalDuration * 1000).toISOString()
  }, [detail, totalDuration])

  const isDirty = useMemo(() => {
    if (!detail) return false
    if (detail.spots.length !== entries.length) return true
    return entries.some((e, i) => e.spotId !== detail.spots[i].spotId)
  }, [detail, entries])

  // Bornes du jour de diffusion du bloc (minuit → minuit local). Sert à ne
  // proposer que les spots dont la campagne couvre ce jour, comme le fait
  // l'auto-composition côté serveur (ad-block.service.ts, OUT_OF_PERIOD).
  const blockDayBounds = useMemo(() => {
    if (!detail) return null
    const s = new Date(detail.startTime)
    const dayStart = new Date(s.getFullYear(), s.getMonth(), s.getDate())
    const dayEnd = new Date(dayStart.getTime() + 86_400_000)
    return { dayStart, dayEnd }
  }, [detail])

  // Un spot est proposable si sa campagne est dans sa période de validité pour
  // le jour du bloc. Les spots déjà dans le bloc restent affichés dans la liste
  // du haut — seul le catalogue d'ajout est restreint.
  const inActivationWindow = useCallback((s: CatalogSpot) => {
    if (!blockDayBounds || !s.campaign?.startDate || !s.campaign?.endDate) return true
    const start = new Date(s.campaign.startDate)
    const end   = new Date(s.campaign.endDate)
    return !(start > blockDayBounds.dayEnd || end < blockDayBounds.dayStart)
  }, [blockDayBounds])

  const filteredCatalog = useMemo(() => {
    const eligible = catalog.filter(inActivationWindow)
    if (!search.trim()) return eligible
    const q = search.toLowerCase()
    return eligible.filter(s =>
      s.title.toLowerCase().includes(q)
      || (s.campaign?.name ?? '').toLowerCase().includes(q)
      || (s.advertiser?.name ?? '').toLowerCase().includes(q),
    )
  }, [catalog, search, inActivationWindow])

  // ── Mutations locales (pas de save tant qu'on ne clique pas Sauver) ────────

  const moveSpot = useCallback((idx: number, dir: -1 | 1) => {
    setEntries(prev => {
      const next = [...prev]
      const target = idx + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
  }, [])

  const reorderSpot = useCallback((activeRowKey: string, overRowKey: string) => {
    setEntries(prev => {
      const oldIndex = prev.findIndex(e => e.rowKey === activeRowKey)
      const newIndex = prev.findIndex(e => e.rowKey === overRowKey)
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || !detail?.isEditable) return
    reorderSpot(String(active.id), String(over.id))
  }, [detail?.isEditable, reorderSpot])

  const removeSpot = useCallback((rowKey: string) => {
    setEntries(prev => prev.filter(e => e.rowKey !== rowKey))
  }, [])

  const addSpot = useCallback((spot: CatalogSpot) => {
    setEntries(prev => [...prev, {
      spotId:         spot.id,
      title:          spot.title,
      durationSec:    spot.durationSec,
      campaignName:   spot.campaign?.name ?? null,
      advertiserName: spot.advertiser?.name ?? null,
      rowKey:         newRowKey(),
    }])
    setShowAdd(false)
    setSearch('')
  }, [])

  // ── Sauvegarde ─────────────────────────────────────────────────────────────

  // Disponibilité des plages d'infoservice voisines pour le réajustement.
  const beforeAvailable = detail?.neighbours.before?.available ?? false
  const afterAvailable  = detail?.neighbours.after?.available ?? false
  const durationChanged = !!detail && totalDuration !== detail.durationSec
  const isShorter       = !!detail && totalDuration < detail.durationSec

  const performSave = useCallback(async (realign?: AdBlockRealignSide) => {
    if (entries.length === 0) {
      pushToast('Un bloc doit contenir au moins un spot', 'warning', 4000)
      return
    }
    setSaving(true)
    setAskSide(false)
    setError(null)
    try {
      await adBlocksApi.updateItemSpots(itemId, entries.map(e => e.spotId), realign)
      pushToast(`Bloc mis à jour · ${entries.length} spot(s) · ${fmtSec(totalDuration)}`, 'success', 5000)
      incrementGridUpdateSeq()
      onSaved()
    } catch (err: any) {
      setError(err?.message ?? 'Sauvegarde impossible')
    } finally {
      setSaving(false)
    }
  }, [itemId, entries, totalDuration, pushToast, incrementGridUpdateSeq, onSaved])

  // Décide du côté à réajuster au moment d'enregistrer : les deux plages
  // possibles → on demande ; une seule → on l'utilise ; aucune → on comble le
  // temps libéré (raccourcissement) ou on tente le chemin classique.
  const handleSaveClick = useCallback(() => {
    if (!durationChanged) { performSave(); return }
    if (beforeAvailable && afterAvailable) { setAskSide(true); return }
    if (afterAvailable)  { performSave('AFTER'); return }
    if (beforeAvailable) { performSave('BEFORE'); return }
    if (isShorter)       { performSave('FILL'); return }
    performSave()
  }, [durationChanged, beforeAvailable, afterAvailable, isShorter, performSave])

  // ── Rendu ──────────────────────────────────────────────────────────────────

  return (
    <ModalShell className="agenda-editor ad-block-editor" closeDisabled={saving} onClose={onClose} size="wide" title="Modifier le bloc pub">

        {/* ── Header ────────────────────────────────────────────────────── */}
        {detail && (
          <div className="ad-block-editor__intro">
            Début : <strong>{fmtTime(detail.startTime)}</strong>
            {' · '}
            Durée actuelle : <strong>{fmtSec(detail.durationSec)}</strong>
          </div>
        )}

        {/* ── Verrou éditabilité ──────────────────────────────────────── */}
        {detail && !detail.isEditable && (
          <div className="agenda-editor__notice agenda-editor__notice--flush is-warning" role="status">
            <span>⚠</span>
            <span>Ce bloc est en cours de diffusion ou déjà diffusé — il ne peut plus être modifié.</span>
          </div>
        )}

        {/* ── Erreur ────────────────────────────────────────────────────── */}
        {error && (
          <div className="agenda-editor__notice agenda-editor__notice--flush is-danger" role="alert">
            ⚠ {error}
          </div>
        )}

        {/* ── Liste des spots ───────────────────────────────────────────── */}
        <main className="ad-block-editor__body">
          {loading ? (
            <div className="agenda-editor__loading agenda-editor__loading--spacious"><Spinner /> Chargement…</div>
          ) : entries.length === 0 ? (
            <div className="agenda-editor__empty">
              Le bloc est vide. Ajoutez au moins un spot avant de sauvegarder.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={entries.map(e => e.rowKey)} strategy={verticalListSortingStrategy}>
                <div className="ad-spot-list">
                  {entries.map((e, idx) => (
                    <SpotRow
                      key={e.rowKey}
                      entry={e}
                      index={idx}
                      isLast={idx === entries.length - 1}
                      disabled={!detail?.isEditable}
                      onMoveUp={() => moveSpot(idx, -1)}
                      onMoveDown={() => moveSpot(idx, 1)}
                      onRemove={() => removeSpot(e.rowKey)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Bouton ajout / panel ajout */}
          {!showAdd && detail?.isEditable && (
            <button
              onClick={() => setShowAdd(true)}
              className="ad-spot-add-trigger"
            >
              + Ajouter un spot
            </button>
          )}

          {showAdd && detail?.isEditable && (
            <AddSpotPanel
              catalog={filteredCatalog}
              search={search}
              onSearchChange={setSearch}
              onPick={addSpot}
              onCancel={() => { setShowAdd(false); setSearch('') }}
            />
          )}
        </main>

        {/* ── Footer (récap + actions) ──────────────────────────────────── */}
        <footer className="ad-block-editor__footer">
          <div className="ad-block-editor__summary">
            <div>
              {entries.length} spot{entries.length !== 1 ? 's' : ''} ·
              <strong className="ad-block-editor__duration">
                {fmtSec(totalDuration)}
              </strong>
              {blackDuration > 0 && (
                <span className="ad-block-editor__black-duration">
                  dont {fmtSec(blackDuration)} de noir
                </span>
              )}
            </div>
            {detail && newEndTime && (
              <div className="ad-block-editor__end-time">
                Nouvelle fin du bloc : <strong>{fmtTime(newEndTime)}</strong>
                {totalDuration !== detail.durationSec && (
                  <span className="ad-block-editor__duration-delta">
                    ({totalDuration > detail.durationSec ? '+' : ''}{totalDuration - detail.durationSec}s)
                  </span>
                )}
              </div>
            )}
            {durationChanged && (beforeAvailable || afterAvailable) && (
              <div className="ad-block-editor__realign-hint">
                {isShorter ? 'Le temps libéré sera rendu à l’infoservice voisin.' : 'Le temps sera repris sur l’infoservice voisin.'}
              </div>
            )}
            {durationChanged && !beforeAvailable && !afterAvailable && isShorter && (
              <div className="ad-block-editor__realign-hint">
                Aucun infoservice voisin — le temps libéré sera comblé par une plage d’infoservice.
              </div>
            )}
          </div>

          {askSide ? (
            <div className="ad-block-editor__side-choice" role="group" aria-label="Côté à réajuster">
              <span className="ad-block-editor__side-choice-label">Reprendre le temps sur l’infoservice&nbsp;:</span>
              <button type="button" className="tp-button" onClick={() => performSave('BEFORE')} disabled={saving}>
                Avant le bloc
              </button>
              <button type="button" className="tp-button tp-button--info" onClick={() => performSave('AFTER')} disabled={saving}>
                Après le bloc
              </button>
              <button type="button" className="tp-button" onClick={() => setAskSide(false)} disabled={saving}>
                Annuler
              </button>
            </div>
          ) : (
            <>
              <button type="button" className="tp-button" onClick={onClose} disabled={saving}>
                Annuler
              </button>
              <button
                type="button"
                className="tp-button tp-button--info"
                data-state={saving ? 'loading' : undefined}
                onClick={handleSaveClick}
                disabled={!isDirty || saving || !detail?.isEditable || entries.length === 0}
              >
                {saving ? 'Sauvegarde…' : 'Enregistrer'}
              </button>
            </>
          )}
        </footer>
    </ModalShell>
  )
})

// ─── Sous-composants ─────────────────────────────────────────────────────────

const SpotRow = memo(function SpotRow({
  entry, index, isLast, disabled, onMoveUp, onMoveDown, onRemove,
}: {
  entry:      SpotEntry
  index:      number
  isLast:     boolean
  disabled:   boolean
  onMoveUp:   () => void
  onMoveDown: () => void
  onRemove:   () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.rowKey, disabled })

  return (
    <div
      ref={setNodeRef}
      className={`ad-spot-row${isDragging ? ' is-dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div className="ad-spot-row__index">
        {index + 1}
      </div>

      <button
        type="button"
        disabled={disabled}
        title="Deplacer"
        {...attributes}
        {...listeners}
        className="ad-spot-row__drag"
      >
        ::
      </button>

      <div className="ad-spot-row__main">
        <div className="ad-spot-row__title">
          {formatAdSpotLabel(entry)}
        </div>
        {entry.advertiserName && (
          <div className="ad-spot-row__meta">
            {entry.advertiserName}
          </div>
        )}
      </div>

      <div className="ad-spot-row__duration">
        {fmtSec(entry.durationSec)}
      </div>

      <div className="ad-spot-row__actions">
        <ArrowBtn label="▲" onClick={onMoveUp}   disabled={disabled || index === 0} title="Monter" />
        <ArrowBtn label="▼" onClick={onMoveDown} disabled={disabled || isLast}      title="Descendre" />
        <button
          onClick={onRemove}
          disabled={disabled}
          title="Retirer"
          className="ad-spot-row__button ad-spot-row__button--remove"
        >
          ×
        </button>
      </div>
    </div>
  )
})

const ArrowBtn = memo(function ArrowBtn({ label, onClick, disabled, title }: {
  label:    string
  onClick:  () => void
  disabled: boolean
  title:    string
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="ad-spot-row__button"
    >
      {label}
    </button>
  )
})

const AddSpotPanel = memo(function AddSpotPanel({
  catalog, search, onSearchChange, onPick, onCancel,
}: {
  catalog:        CatalogSpot[]
  search:         string
  onSearchChange: (v: string) => void
  onPick:         (s: CatalogSpot) => void
  onCancel:       () => void
}) {
  return (
    <div className="ad-spot-picker">
      <div className="ad-spot-picker__toolbar">
        <input
          autoFocus
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Filtrer par titre, campagne, annonceur…"
          className="tp-input ad-spot-picker__search"
        />
        <button type="button" className="tp-button" onClick={onCancel}>
          Fermer
        </button>
      </div>

      <div className="ad-spot-picker__list">
        {catalog.length === 0 ? (
          <div className="agenda-editor__empty agenda-editor__empty--compact">
            {search.trim()
              ? 'Aucun spot ne correspond à la recherche'
              : 'Aucun spot dont la campagne est active à cette date'}
          </div>
        ) : catalog.map(s => (
          <button
            key={s.id}
            onClick={() => onPick(s)}
            className="ad-spot-picker__option"
          >
            <div className="ad-spot-picker__main">
              <div className="ad-spot-picker__title">
                {formatAdSpotLabel(s)} <MissingFileBadge presence={s.presence} />
              </div>
              {s.advertiser?.name && (
                <div className="ad-spot-picker__meta">
                  {s.advertiser.name}
                </div>
              )}
            </div>
            <div className="ad-spot-picker__duration">
              {fmtSec(s.durationSec)}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
})
